/** @jsxImportSource @emotion/react */
import { useForm } from "react-hook-form";
import "react-medium-image-zoom/dist/styles.css";
import { pdf } from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Zoom from "react-medium-image-zoom";
import { Link, useNavigate } from "react-router-dom";
import Canva from "../Canva/Canva";
import Konva from "../Canva/Konva";
import Point from "../Canva/Point";
import Polygon from "../Canva/Polygon";
import { getHeatAreaPadding_MonteCarlo_main } from "../Canva/getHeatAreaPadding_MonteCarlo";
import store from "../Common/Store";
import Panel from "../Common/Panel";
import TipBox from "../Common/TipBox";
import formatWattage from "../Common/formatWattage";
import { buildMatDisplayEntries } from "../Common/matLayoutColors";
import {
  getTotalMatOutput,
  requiresContactor,
} from "../Common/matElectricalLoad";
import {
  isHeatMatApp,
  sendPdfBlobToHeatMatApp,
} from "../Common/heatMatAppBridge";

import { PDFDownload, MyDocument } from "./PDFGen";
import DetailsPopup from "./DetailsPopup";

function EmailDialogIcon({ name }) {
  const paths = {
    close: <path d="M6 6l12 12M18 6 6 18" />,
    user: (
      <>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.5 20c.5-4 2.7-6 6.5-6s6 2 6.5 6" />
      </>
    ),
    email: (
      <>
        <rect x="3" y="5.5" width="18" height="13" rx="2" />
        <path d="M4 7l8 6 8-6" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v6M12 7.5v.5" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v11M8 10l4 4 4-4" />
        <path d="M4 17v3h16v-3" />
      </>
    ),
    send: <path d="M21 3L9.5 14.5M21 3l-7 18-4.5-6.5L3 10l18-7z" />,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none">
      <g
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {paths[name]}
      </g>
    </svg>
  );
}

function ResultIcon({ name, className = "" }) {
  const materialPaths = {
    pdf: "M340.67-440.67h38v-82H424q16.15 0 27.07-10.92Q462-544.52 462-560.67V-606q0-16.15-10.93-27.07Q440.15-644 424-644h-83.33v203.33Zm38-120V-606H424v45.33h-45.33Zm126 120h82.66q15.67 0 26.84-10.92 11.16-10.93 11.16-27.08V-606q0-16.15-11.16-27.07Q603-644 587.33-644h-82.66v203.33Zm38-38V-606h44.66v127.33h-44.66Zm128.66 38h38v-82H756v-38h-46.67V-606H756v-38h-84.67v203.33ZM280-213.33q-27 0-46.83-19.84Q213.33-253 213.33-280v-533.33q0-27 19.84-46.84Q253-880 280-880h533.33q27 0 46.84 19.83Q880-840.33 880-813.33V-280q0 27-19.83 46.83-19.84 19.84-46.84 19.84H280Zm0-66.67h533.33v-533.33H280V-280ZM146.67-80q-27 0-46.84-19.83Q80-119.67 80-146.67v-600h66.67v600h600V-80h-600ZM280-813.33V-280v-533.33Z",
    heat: "M153.67-153.44Q120-186.89 120-234.67V-640h66.67v405.33q0 20.4 14.16 34.2 14.17 13.8 34.55 13.8 20.38 0 34.17-13.8 13.78-13.8 13.78-34.2v-490q0-48.05 33.63-81.69T398.63-840q48.04 0 81.37 33.64 33.33 33.64 33.33 81.69v490q0 20.4 14.17 34.2 14.17 13.8 34.55 13.8 20.38 0 34.16-13.8 13.79-13.8 13.79-34.2v-490q0-48.05 33.63-81.69T725.29-840q48.04 0 81.38 33.64Q840-772.72 840-724.67V-320h-66.67v-404.67q0-20.33-13.78-34.5-13.79-14.16-34.17-14.16-20.38 0-34.55 14.16-14.16 14.17-14.16 34.5v490q0 47.78-33.49 81.23Q609.69-120 561.84-120q-47.84 0-81.51-33.44-33.66-33.45-33.66-81.23v-490q0-20.33-13.79-34.5-13.79-14.16-34.17-14.16-20.38 0-34.54 14.16Q350-745 350-724.67v490q0 47.78-33.49 81.23Q283.02-120 235.18-120q-47.85 0-81.51-33.44Z",
    room: "M202.67-120q-34.34 0-58.5-24.17Q120-168.33 120-202.67v-542q0-34.33 32-47.5 32-13.16 56.67 11.5l60 60-46 46L250-647.33l46-46 112.67 112.66-46 46L390-507.33l46-46L549.33-440l-46 46 27.34 27.33 46-46L689.33-300l-46 46 27.34 27.33 46-46L773.33-216q26.67 26.67 12.84 61.33Q772.33-120 736-120H202.67Zm10.66-93.33h419.34L213.33-632.67v419.34Z",
    details: "M278-279.33h276.67V-346H278v66.67Zm0-167.34h404v-66.66H278v66.66ZM278-614h404v-66.67H278V-614Zm-91.33 494q-27 0-46.84-19.83Q120-159.67 120-186.67v-586.66q0-27 19.83-46.84Q159.67-840 186.67-840h586.66q27 0 46.84 19.83Q840-800.33 840-773.33v586.66q0 27-19.83 46.84Q800.33-120 773.33-120H186.67Zm0-66.67h586.66v-586.66H186.67v586.66Zm0-586.66v586.66-586.66Z",
  };

  if (materialPaths[name]) {
    return (
      <svg
        className={className}
        viewBox="0 -960 960 960"
        aria-hidden="true"
        fill="currentColor"
      >
        <path d={materialPaths[name]} />
      </svg>
    );
  }

  const paths = {
    check: <polyline points="5 12 10 17 20 7" />,
    back: (
      <path
        d="M14.71 6.71c-.39-.39-1.02-.39-1.41 0L8.71 11.3c-.39.39-.39 1.02 0 1.41l4.59 4.59c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L10.83 12l3.88-3.88c.39-.39.38-1.03 0-1.41z"
        fill="currentColor"
        stroke="none"
      />
    ),
    email: (
      <>
        <rect x="3" y="6" width="18" height="13" rx="2" />
        <path d="M4 8l8 6 8-6" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v6M12 7.5v.5" />
      </>
    ),
    phone: <path d="M7 4l3 4-2 2c1.5 3 3 4.5 6 6l2-2 4 3-2 4C10 20 4 14 3 6z" />,
    home: (
      <path
        d="M10 19v-5h4v5c0 .55.45 1 1 1h3c.55 0 1-.45 1-1v-7h1.7c.46 0 .68-.57.33-.87L12.67 3.6c-.38-.34-.96-.34-1.34 0l-8.36 7.53c-.34.3-.13.87.33.87H5v7c0 .55.45 1 1 1h3c.55 0 1-.45 1-1z"
        fill="currentColor"
        stroke="none"
      />
    ),
  };

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

const buildResultPolyData = () => {
  if (!store.polygon) {
    return null;
  }

  const polygonPoints = [
    ...store.polygon.points,
    store.polygon.points[0],
  ];

  return {
    room_points: store.polygon.getPointsCoords(),
    room_side_length: Canva.getSidesLength(polygonPoints),
    room_side_center: Canva.getSidesCenters(polygonPoints),
    room_shift: { x: store.polygon.x, y: store.polygon.y },
    room_full_area: Polygon.getAreaByShoelaceFormula(store.polygon.points),
    floor1: store.floor1,
    spots: store.spots.map((spot) => ({
      spot_point: spot.getPointsCoords(),
      spot_area: Polygon.getAreaByShoelaceFormula(spot.points),
      spot_shift: { x: spot.x, y: spot.y },
      type: spot.type,
    })),
    thermostats: store.thermo.map((thermostat) => ({
      coord: {
        x: thermostat.x(),
        y: thermostat.y(),
        id: thermostat.attrs.id,
      },
    })),
    pixels_per_meter: Canva.scale,
  };
};

function Result() {
  const navigate = useNavigate();
  const [page, setPageState] = useState("/");
  const [detailsPopup, setDetailsPopup] = useState(0);
  const [emailPopup, setEmailPopup] = useState(0);
  const [polyData] = useState(buildResultPolyData);
  const [sending, setSending] = useState(0);
  const [error, setError] = useState(0);
  const [thermostatDescriptionExpanded, setThermostatDescriptionExpanded] =
    useState(false);
  const { t } = useTranslation();
  const runningInHeatMatApp = isHeatMatApp();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleGeneratePDF = async (doc) => {
    const blob = await pdf(doc).toBlob();
    return blob;
  };

  const handleNativePdf = async () => {
    const pdfData = await handleGeneratePDF(<MyDocument store={store} />);
    await sendPdfBlobToHeatMatApp(pdfData);
  };

  // Determine "back" page from mode
  useEffect(() => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const c = Number(url.searchParams.get("mode"));
    if (c === 1) {
      setPageState("/standartmode");
    } else {
      setPageState("/imgmode");
    }
  }, []);

  // Prepare polyData, store snapshots and persist to localStorage
  useEffect(() => {
    if (store.polygon && polyData) {
      // Clean bindings
      store.spots.forEach((spot) => {
        spot.unbindPoints();
      });
      store.polygon.unbindPoints();

      // Prepared data for PDF
      store.spotsPrep = store.spots.map((itm) => {
        const area = Polygon.getAreaByShoelaceFormula(itm.points);
        return {
          spot_point: itm.getPointsCoords(),
          spot_area: area,
          spot_shift: { x: itm.x, y: itm.y },
          type: itm.type,
        };
      });

      store.thermoPrep = store.thermo.map((itm) => ({
        coord: { x: itm.x(), y: itm.y(), id: itm.attrs.id },
      }));

      store.room_heated_area_padding = getHeatAreaPadding_MonteCarlo_main(
        store,
        Polygon,
        Point,
        Konva,
        Canva
      );
      store.room_area = store.polygon
        ? Polygon.getAreaByShoelaceFormula(store.polygon.points)
        : 0;

      store.polygon.getPointsCoordsPrep = store.polygon.getPointsCoords();

      if (store.polygon.points) {
        store.polygon.points.forEach((point) => {
          delete point.line;
        });
      }

      try {
        localStorage.setItem("store", JSON.stringify(store));
      } catch (e) {
        // The in-memory result is still valid. Do not blank the result page if
        // a browser has an unusually small or already-full storage quota.
        console.warn("Failed to persist result store", e);
      }
    } else {
      navigate("/");
    }
  }, [navigate, polyData]);

  function onSubmit(sendData) {
    if (polyData !== null && sending === 0) {
      setSending(1);
      handleGeneratePDF(<MyDocument store={store} />).then((pdfData) => {
        const formData = new FormData();
        formData.append(
          "pdf",
          pdfData,
          "HEATMAT_installation_recommendations.pdf"
        );
        formData.append("name", sendData.name);
        formData.append("email", sendData.email);

        fetch(process.env.REACT_APP_BE_HOST + "/user/checkin", {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        })
          .then((res) => {
            setSending(0);
            if (res.ok) {
              setError(1);
              setTimeout(() => setError(0), 2000);
              return res.json();
            } else {
              setError(2);
              setTimeout(() => setError(0), 2000);
              return null;
            }
          })
          .then((data) => data)
          .catch(() => {
            setError(2);
            setTimeout(() => setError(0), 2000);
          });
      });
    } else {
      alert("Wait... Data is still loading...");
    }
  }

  const matEntries = store.mat_data?.matParams
    ? Object.values(store.mat_data.matParams)
    : [];
  const matDisplayEntries = buildMatDisplayEntries(store.mat_data);
  const reportedCoverage = Number(store.mat_data?.props?.TotalMatCoverage);
  const calculatedCoverage = matEntries.reduce(
    (total, mat) => total + (Number(mat.Size) || 0),
    0
  );
  const totalOutput = getTotalMatOutput(store.mat_data);
  const exceedsThermostatLoad = requiresContactor(store.mat_data);
  const thermostatInfo = store.thermoInfo || null;
  const thermostatImage =
    thermostatInfo?.img && thermostatInfo.img !== "test"
      ? thermostatInfo.img
      : "/img/thermo1.png";
  const heatingArea = Number.isFinite(reportedCoverage) && reportedCoverage > 0
    ? reportedCoverage
    : calculatedCoverage;
  return (
    <div className="result-page">
      <div className="result-content">
        <header className="result-hero">
          <span className="result-status-icon">
            <ResultIcon name="check" />
          </span>
          <div className="result-hero-copy">
            <h1>{t("Calculation complete")}</h1>
            <p>{t("Here is your recommended heating mat layout and product.")}</p>
          </div>
          <div className="result-header-actions">
            <button
              type="button"
              className="result-header-back"
              onClick={() => {
                store.thermo = [];
                navigate(page + "?thermo=1");
              }}
            >
              <ResultIcon name="back" />
              <span>{t("Back")}</span>
            </button>
            <Link to="/" className="result-home-button">
              <ResultIcon name="home" />
              <span>{t("Return to home")}</span>
            </Link>
          </div>
        </header>

        {store.polygon && polyData !== null ? (
          <>
            <main className="result-dashboard">
              <section className="result-card result-plan-card">
                <div className="result-plan-panel">
                  {store.final_img && (
                    <Zoom>
                      <img
                        alt={t("Recommended heating mat layout")}
                        className="result-plan-image"
                        src={store.final_img}
                      />
                    </Zoom>
                  )}
                </div>
              </section>

              <section className="result-summary-column">
                <div className="result-metrics">
                  <article className="result-card result-metric-card">
                    <span className="result-metric-icon">
                      <ResultIcon name="room" />
                    </span>
                    <div>
                      <span>{t("Room area")}</span>
                      <strong>
                        {parseFloat(polyData.room_full_area).toFixed(2)} m<sup>2</sup>
                      </strong>
                    </div>
                  </article>
                  <article className="result-card result-metric-card">
                    <span className="result-metric-icon">
                      <ResultIcon name="heat" />
                    </span>
                    <div>
                      <span>{t("Floor heating area")}</span>
                      <strong>
                        {heatingArea > 0 ? heatingArea.toFixed(2) : "---"} m<sup>2</sup>
                      </strong>
                    </div>
                  </article>
                </div>

                {store.final_img && (
                  <div className="result-actions">
                    <button
                      type="button"
                      className="result-action-button"
                      onClick={() => setDetailsPopup(1)}
                    >
                      <ResultIcon name="details" />
                      <span>{t("Details")}</span>
                    </button>
                    {runningInHeatMatApp ? (
                      <button
                        type="button"
                        className="result-action-button"
                        onClick={handleNativePdf}
                      >
                        <ResultIcon name="pdf" />
                        <span>{t("Save PDF")}</span>
                      </button>
                    ) : (
                      <Link to="/pdf-page" target="_blank">
                      <button type="button" className="result-action-button">
                        <ResultIcon name="pdf" />
                        <span>{t("Save PDF")}</span>
                      </button>
                      </Link>
                    )}
                    <button
                      type="button"
                      className="result-action-button"
                      onClick={() => setEmailPopup(1)}
                    >
                      <ResultIcon name="email" />
                      <span>{t("Send by email 2")}</span>
                    </button>
                  </div>
                )}

                <div className="result-products">
                  {matDisplayEntries.map(({ mat, color, key }) => {
                    return (
                      <article
                        className="result-card result-product-card result-product-card--visual"
                        key={key}
                      >
                        <div className="result-product-heading">
                          <span
                            className="result-product-dot"
                            aria-hidden="true"
                            style={{ backgroundColor: color }}
                          />
                          <div>
                            <strong>1 x {mat.MatId}</strong>
                            <span>
                              {store.mat_data?.chosenMatDescription?.type
                                ? t(store.mat_data.chosenMatDescription.type)
                                : t("Heating mat")}
                            </span>
                          </div>
                        </div>
                        <div className="result-product-stats">
                          <div>
                            <strong>{Number(mat.Size).toFixed(2)} m<sup>2</sup></strong>
                            <span>{t("Coverage")}</span>
                          </div>
                          <div>
                            <strong>{formatWattage(mat.Wattage)}W</strong>
                            <span>{t("Output")}</span>
                          </div>
                        </div>
                        <div className="result-product-image">
                          <img src="/img/heating-mat.jpg" alt={t("Heating mat")} />
                        </div>
                      </article>
                    );
                  })}

                  {thermostatInfo && (
                    <article className="result-card result-product-card result-product-card--visual result-thermostat-card">
                      <div className="result-product-heading">
                        <span
                          className="result-product-dot"
                          aria-hidden="true"
                        />
                        <div>
                          <strong>1 x {thermostatInfo.name}</strong>
                          <span
                            id="result-thermostat-description"
                            className={`result-thermostat-description${
                              thermostatDescriptionExpanded
                                ? " result-thermostat-description--expanded"
                                : ""
                            }`}
                          >
                            {thermostatInfo.description || t("Thermostat")}
                          </span>
                          {thermostatInfo.description && (
                            <button
                              type="button"
                              className="result-description-toggle"
                              aria-expanded={thermostatDescriptionExpanded}
                              aria-controls="result-thermostat-description"
                              aria-label={t(
                                thermostatDescriptionExpanded
                                  ? "Hide full description"
                                  : "Show full description"
                              )}
                              title={t(
                                thermostatDescriptionExpanded
                                  ? "Hide full description"
                                  : "Show full description"
                              )}
                              onClick={() =>
                                setThermostatDescriptionExpanded(
                                  (expanded) => !expanded
                                )
                              }
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41Z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="result-product-image result-product-image--thermostat">
                        <Zoom>
                          <img
                            src={thermostatImage}
                            alt={`${thermostatInfo.name} ${t("Thermostat")}`}
                          />
                        </Zoom>
                      </div>
                    </article>
                  )}
                </div>

                {(matEntries.length > 1 || exceedsThermostatLoad) && (
                  <article
                    className={`result-card result-output-summary${
                      exceedsThermostatLoad
                        ? " result-output-summary--warning"
                        : ""
                    }`}
                  >
                    <div className="result-output-summary__total">
                      <span>{t("Total connected output")}</span>
                      <strong>{formatWattage(totalOutput)}W</strong>
                    </div>
                    {exceedsThermostatLoad && (
                      <p>{t("thermostat_load_warning")}</p>
                    )}
                  </article>
                )}

              </section>
            </main>

            {store.final_img && (
              <section className="result-card result-advisory">
                <div className="result-advisory-copy">
                  <ResultIcon name="info" className="result-advisory-icon" />
                  <div>
                    <h2>{t("About your layout")}</h2>
                    <p>{t("result_layout_description")}</p>
                  </div>
                </div>
                <div className="result-support">
                  <p className="result-support-intro">
                    {t("Please contact our technical support team on")}
                  </p>
                  <div className="result-support-phone-row">
                    <ResultIcon name="phone" />
                    <a href="tel:01444247020">01444 247020</a>
                    <span>{t("for further guidance.")}</span>
                  </div>
                </div>
              </section>
            )}

            <div className="result-home-row">
              <Link to="/" className="result-home-button">
                <ResultIcon name="home" />
                <span>{t("Return to home")}</span>
              </Link>
            </div>
          </>
        ) : (
          null
        )}
      </div>

      {/* Details popup */}
      {detailsPopup === 1 && (
        <DetailsPopup
          setDetailsPopup={setDetailsPopup}
          store={store}
          onNativePdf={handleNativePdf}
        />
      )}

      {/* Email popup */}
      {emailPopup === 1 && (
        <div
          className="result-email-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setEmailPopup(0);
            }
          }}
        >
          <section
            className="result-email-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="result-email-title"
          >
            <button
              type="button"
              className="result-email-close"
              aria-label={t("Close")}
              onClick={() => setEmailPopup(0)}
            >
              <EmailDialogIcon name="close" />
            </button>

            <header className="result-email-header">
              <h2 id="result-email-title">{t("Save or send your plan")}</h2>
              <p>{t("email_modal_description")}</p>
            </header>

            <form
              id="user-create-form"
              className="result-email-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <label className="result-email-field">
                <span>{t("Name")}</span>
                <span
                  className={`result-email-input${
                    errors.name ? " result-email-input--error" : ""
                  }`}
                >
                  <EmailDialogIcon name="user" />
                  <input
                    type="text"
                    placeholder={t("Enter your name")}
                    {...register("name", { required: true, minLength: 2 })}
                  />
                </span>
                {errors.name && (
                  <small className="result-email-error">
                    {t("Incorrect name")}
                  </small>
                )}
              </label>

              <label className="result-email-field">
                <span>{t("Email address")}</span>
                <span
                  className={`result-email-input${
                    errors.email ? " result-email-input--error" : ""
                  }`}
                >
                  <EmailDialogIcon name="email" />
                  <input
                    type="email"
                    placeholder={t("Enter your email address")}
                    {...register("email", {
                      required: true,
                      minLength: 4,
                      pattern:
                        /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
                    })}
                  />
                </span>
                {errors.email && (
                  <small className="result-email-error">
                    {t("Incorrect Email")}
                  </small>
                )}
              </label>

              <div className="result-email-note">
                <EmailDialogIcon name="info" />
                <p>
                  <strong>{t("Save PDF to download instantly.")}</strong>
                  <span>{t("email_modal_attachment_note")}</span>
                </p>
              </div>

              <div className="result-email-status" aria-live="polite">
                {error === 1 && (
                  <span className="result-email-status--success">
                    {t("Sent successfully")}
                  </span>
                )}
                {error === 2 && (
                  <span className="result-email-status--error">
                    {t("An error has occurred")}
                  </span>
                )}
                {sending === 1 && <span>{t("Sending...")}</span>}
              </div>

              <div className="result-email-actions">
                <PDFDownload
                  PDF={<MyDocument store={store} />}
                  btn={
                    <button
                      type="button"
                      className="result-email-action result-email-action--secondary"
                    >
                      <EmailDialogIcon name="download" />
                      <span>{t("Save PDF")}</span>
                    </button>
                  }
                />

                <button
                  type="submit"
                  className="result-email-action result-email-action--primary"
                  disabled={sending === 1}
                >
                  <EmailDialogIcon name="send" />
                  <span>{t("Send by email")}</span>
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      <Panel page="6" />
      <TipBox
        tip="6"
        funcPrev={() => {
          store.thermo = [];
          navigate(page + "?thermo=1");
        }}
      />
    </div>
  );
}

export default Result;
